import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatINR, methodLabel } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Textarea } from "./ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function EditPaymentDialog({ payment, open, onOpenChange, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [billNo, setBillNo] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (payment) {
      setAmount(String(payment.amount));
      setMethod(payment.method);
      setNote(payment.online_note || "");
      setBillNo(payment.bill_no || "");
    }
  }, [payment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!payment) return null;

  const billNoValid = method !== "cash" || /^\d{4}$/.test(billNo);

  const submit = async () => {
    if (!billNoValid) {
      toast.error("Cash payment requires a 4-digit bill number");
      return;
    }
    setLoading(true);
    try {
      await api.put(`/payments/${payment.id}`, {
        amount: Number(amount),
        method,
        online_note: method === "cash" ? "" : note,
        bill_no: method === "cash" ? billNo : "",
      });
      toast.success("Payment updated");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-payment-dialog">
        <DialogHeader>
          <DialogTitle>Edit Payment</DialogTitle>
          <DialogDescription>
            {payment.customer_name} · {payment.customer_code} · {payment.service_type === "cable" ? "Cable TV" : "Internet"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Amount (₹)</Label>
            <Input data-testid="edit-payment-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label>Method</Label>
            <RadioGroup value={method} onValueChange={setMethod} className="grid grid-cols-3 gap-2 mt-2">
              {["cash", "gpay", "phonepe"].map((m) => (
                <label key={m} htmlFor={`ep-${m}`} className={`border rounded-md px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${method === m ? "border-red-500 bg-red-50" : "border-gray-200"}`}>
                  <RadioGroupItem id={`ep-${m}`} value={m} data-testid={`edit-payment-method-${m}`} />
                  <span className="capitalize">{methodLabel(m)}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          {method === "cash" && (
            <div>
              <Label>Bill No (4-digit invoice)</Label>
              <Input
                data-testid="edit-payment-bill-no"
                inputMode="numeric"
                maxLength={4}
                value={billNo}
                onChange={(e) => setBillNo(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className={billNo && !billNoValid ? "border-red-500" : ""}
              />
              {billNo && !billNoValid && (
                <div className="text-xs text-red-600 mt-1">Must be exactly 4 digits.</div>
              )}
            </div>
          )}
          {(method === "gpay" || method === "phonepe") && (
            <div>
              <Label>Online Note</Label>
              <Textarea data-testid="edit-payment-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="edit-payment-save" disabled={loading || !amount || !billNoValid} onClick={submit} className="bg-red-600 hover:bg-red-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
